import GenericTrackListView from '../components/common/GenericTrackListView';
import { QUEUE_TYPE_ALBUM, ENTITY_TYPE_ALBUM } from '../constants';
import { loadAlbumWithExtras } from '../actions/album';
import { showAddToPlaylistModal, showModal } from '../actions/ui';
import {
  selectAlbum,
  selectAlbumIsPlaying,
  selectAlbumIsQueued,
  selectAlbumImageAnnotation,
  selectAlbumIdFromSlug,
  selectSimilarAlbums,
  selectAlbumFacts,
} from '../selectors/album';

import * as playerActions from '../actions/player';

import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import dataComponent from '../hoc/dataComponent';
import collectibleEntity from '../hoc/collectibleEntity';
import shareableEntity from '../hoc/shareableEntity';
import playlistableEntity from '../hoc/playlistableEntity';
import queueOriginComponent from '../hoc/queueOriginComponent';
import trackedViewComponent from '../hoc/trackedViewComponent';

import { selectCollectionIds } from '../selectors/collection';
import { selectUserIsPatron } from '../selectors/user';

import { defineMessages } from 'react-intl';
import * as analyticsActions from '../actions/analytics';

import { albumVideos } from '../discover-videos';
import OriginalContentVideo from './OriginalContentVideo';

import styles from '../components/common/Albums.css';
import { buildAlbumSchema } from '../lib/googleSchema';
import * as clientActions from '../actions/client';
import { IMGIX_STYLE_4, imgixImageUrl, PLAYABLE_ITEM_FULL_DIMS } from '../lib/imgix';

function fetchData(store, { slug }) {
  return [store.dispatch(loadAlbumWithExtras(slug))];
}

const messages = defineMessages({
  playAllButtonPausedText: {
    id: 'album.play-all.paused.title',
    defaultMessage: 'Play album',
  },
  playAllButtonPlayingText: {
    id: 'album.play-all.playing.title',
    defaultMessage: 'Pause album',
  },
  albumsMetaDescription: {
    id: 'albums.meta.description',
    defaultMessage: 'Listen to {albumName} by {artistName}. Stream now on IDAGIO',
  },
});

function mapStateToProps(state, ownProps) {
  const slug = ownProps.params.slug;
  const album = selectAlbum(state, slug);
  return {
    playlist: album,
    album,

    userIsPatron: selectUserIsPatron(state),
    collectionIds: selectCollectionIds(state),
    imageAnnotation: selectAlbumImageAnnotation(state, slug),
    pathname: ownProps.location.pathname,
    messages: messages,
    bookletUrl: album.bookletUrl,
    recommendations: selectSimilarAlbums(state, slug),
    facts: selectAlbumFacts(state, slug),
  };
}

function getCollectibleEntityDescription(props) {
  return {
    id: props.album.id,
    trackingSource: 'Album',
  };
}

function getQueueOrigin(originId) {
  return {
    type: QUEUE_TYPE_ALBUM,
    id: originId,
  };
}

function mapQueueOriginStateToProps(state, ownProps) {
  const { slug, tracks, trackIds } = ownProps.album;
  return {
    originId: slug,

    tracks,
    trackIds,

    isPlaying: selectAlbumIsPlaying(state, slug),
    isQueued: selectAlbumIsQueued(state, slug),
  };
}

function getTrackingContext(props) {
  return {
    contextType: 'album',
    contextId: props.album.id.toString(),
  };
}

function getShareTrackingContext(props) {
  return {
    sharedContentType: 'Album',
    sharedContentId: props.album.id,
    contextType: 'Album',
    contentId: props.album.id,
  };
}

function getEntityTrackingData(props) {
  return {
    itemType: 'album',
    itemId: props.album.id,
  };
}

// Adds a proper displayName used for tracking
const Album = props => {
  return (
    <React.Fragment>
      <GenericTrackListView
        {...props}
        entityType="album"
        key="album-track-list"
        isOnlyRadioAvailable={__CAPACITOR__ && !props.userIsPatron}
      />
      {albumVideos.find(x => x.id === props.album.slug) ? (
        <div className={styles.albumVideoContainer} id="video">
          <OriginalContentVideo id={props.album.slug} key="album-video" type="album" />
        </div>
      ) : null}
    </React.Fragment>
  );
};

Album.onDataFetch = (store, params, location, intl) => {
  const state = store.getState();
  const albumSlug = params.slug;
  const album = selectAlbum(state, albumSlug);

  store.dispatch(
    clientActions.setHelmet({
      title: `IDAGIO | ${album.title} ${album.upc}`,
      description: intl.formatMessage(messages.albumsMetaDescription, {
        albumName: album.title,
        artistName: album.participants.map(participant => participant.name).join(', '),
      }),
      canonicalRoute: `/albums/${album.slug}`,
      googleSchema: buildAlbumSchema(album, intl),
      ogImage: imgixImageUrl({
        path: album.imageUrl,
        style: IMGIX_STYLE_4,
        width: PLAYABLE_ITEM_FULL_DIMS.w,
        height: PLAYABLE_ITEM_FULL_DIMS.h,
      }),
      ogTitle: album.title,
      ogType: 'music.album',
      keywords: [album.title, ...album.participants.map(x => x.name)].join(', '),
    })
  );

  if (!__CAPACITOR__ || !location.query || !location.query.trackId) {
    return;
  }
  // In capacitor we may have deeplinks that request a track to be played upon startup.
  const requestedTrack = location.query && location.query.trackId;

  store.dispatch(playerActions.rewind());

  const tracks = album.trackIds.map(playerActions.createItem);

  store.dispatch(
    playerActions.setQueueAndPlay(
      getQueueOrigin(albumSlug),
      tracks,
      playerActions.getItemById(requestedTrack, tracks)
    )
  );
};

const trackingProps = (state, params) => {
  return {
    contextType: 'Album',
    contextId: selectAlbumIdFromSlug(state, params.slug),
    contextSlug: params.slug,
  };
};

export default compose(
  trackedViewComponent('Album', trackingProps),
  dataComponent(fetchData),
  connect(mapStateToProps, {
    track: analyticsActions.track,
    showAddToPlaylistModal,
    showModal,
  }),
  shareableEntity(ENTITY_TYPE_ALBUM, getShareTrackingContext),
  collectibleEntity(ENTITY_TYPE_ALBUM, getCollectibleEntityDescription),
  playlistableEntity({
    getEntityTrackingData,
    getTrackingContext,
    entityType: ENTITY_TYPE_ALBUM,
  }),
  queueOriginComponent(getQueueOrigin, mapQueueOriginStateToProps, getTrackingContext)
)(Album);
